<template>
  <b-overlay :show="loading" :opacity="0.6">
    <b-row no-gutters>
      <b-col class="py-2">
        Safety Status:
        <b-badge :variant="saftyBadgeVariant(unit.isSafe)" class="mr-1" style="min-width: 50px">
          {{ safetyText(unit.isSafe) }}
        </b-badge>
      </b-col>
    </b-row>
    <b-row class="mt-3" no-gutters :class="$smallScreen ? 'flex-nowrap' : ''">
      <div>
        <b-form-radio-group
          v-model="filterBy"
          class="mr-3"
          :options="filterOptions"
          buttons
          button-variant="secondary"
        />
      </div>
      <div>
        <b-form-group>
          <b-input-group>
            <b-form-input
              id="filterInput"
              v-model="filter"
              variant="position-relative"
              type="search"
              placeholder="Type to Filter"
              :debounce="500"
            ></b-form-input>
          </b-input-group>
        </b-form-group>
      </div>
      <b-pagination
        v-if="!$smallScreen"
        v-model="currentPage"
        :total-rows="totalRows"
        :per-page="pageSize"
        aria-controls="my-table"
        align="left"
        class="ml-auto w-auto"
      ></b-pagination>
    </b-row>
    <mobile-table v-if="$smallScreen" :items="unitDvirDefects" :fields="fields">
      <template #[`reportedBy`]="{ item }">
        <label :class="`col-head`">Reported By:</label>
        <div class="d-flex">
          <employee-display :employee="item.reportedBy" />
          <span class="pl-1">- {{ item.dateReported | date }}</span>
        </div>
      </template>
      <template #[`resolvedBy`]="{ item }">
        <label :class="`col-head`">Resolved By:</label>
        <div v-if="item.resolvedBy" class="d-flex">
          <employee-display :employee="item.resolvedBy" />
          <span class="pl-1">- {{ item.dateResolved | date }}</span>
        </div>
        <div v-else>{{ NA }}</div>
      </template>
      <template #[`daysAgo`]=""><span /></template>
      <template #[`dateReported`]=""><span /></template>
      <template #[`dateResolved`]=""><span /></template>
    </mobile-table>
    <b-table
      v-else
      striped
      class="border"
      sticky-header="50vh"
      :items="unitDvirDefects"
      :fields="fields"
      :per-page="pageSize"
      :current-page="currentPage"
      :filter="filter"
      :sort-by.sync="sortBy"
      show-empty
      empty-text="No DVIR defects found."
      empty-filtered-text="No results found."
      @filtered="onFiltered"
    >
      <template #cell(dateReported)="{ item }">
        <div>{{ item.dateReported | date }}</div>
      </template>
      <template #cell(reportedBy)="{ item }">
        <employee-display :employee="item.reportedBy" />
      </template>
      <template #cell(dateResolved)="{ item }">
        <div>{{ item.dateResolved | date }}</div>
      </template>
      <template #cell(resolvedBy)="{ item }">
        <employee-display :employee="item.resolvedBy" />
      </template>
    </b-table>
  </b-overlay>
</template>

<script>
// components
import EmployeeDisplayComponent from '@/shared/components/ui/EmployeeDisplayComponent';
// vuex
import { UnitActions, UnitGetters } from '@/shared/store/unit/types';
import { mapActions, mapGetters } from 'vuex';
// helper
import moment from 'moment';

export default {
  name: 'UnitDvirDefectsPage',
  components: {
    'employee-display': EmployeeDisplayComponent,
    'mobile-table': () => import('@/shared/components/MobileTableComponent')
  },
  data: function () {
    return {
      NA: 'N/A',
      pageSize: 50,
      currentPage: 1,
      totalRows: 0,
      filter: null,
      sortBy: null,
      loading: false,
      filterBy: this.$route.query.filterBy ? this.$route.query.filterBy : 'Open',
      filterOptions: [
        { text: 'All', value: null },
        { text: 'Open', value: 'Open' },
        { text: 'Resolved', value: 'Resolved' }
      ]
    };
  },
  computed: {
    ...mapGetters({
      unit: UnitGetters.GET_UNIT
    }),
    unitDvirDefects() {
      const unitDvirDefects = this.$store.getters[UnitGetters.GET_UNIT_DVIR_DEFECTS];
      if (this.filterBy === 'Open') {
        return unitDvirDefects.filter(i => i.dateResolved == null);
      } else if (this.filterBy === 'Resolved') {
        return unitDvirDefects.filter(i => i.dateResolved != null);
      } else {
        return unitDvirDefects;
      }
    },
    fields() {
      const fields = [];
      fields.push({
        key: 'daysAgo',
        label: 'Days',
        sortable: true,
        sortByFormatted: true,
        filterByFormatted: true,
        formatter: (value, key, item) => {
          return this.daysAgo(item);
        }
      });
      fields.push({ key: 'name', label: 'Defect', sortable: true });
      fields.push({ key: 'remark', label: 'Defect Remarks', sortable: true });
      fields.push({
        key: 'status',
        label: 'Status',
        sortable: true,
        sortByFormatted: true,
        filterByFormatted: true,
        formatter: (value, key, item) => {
          return item.dateResolved == null ? 'OPEN' : 'RESOLVED';
        }
      });
      fields.push({ key: 'reportedBy', label: 'Reported By', sortable: true });
      fields.push({ key: 'dateReported', label: 'Date Reported', sortable: true });
      fields.push({ key: 'resolvedBy', label: 'Resolved By', sortable: true, collapsible: true });
      fields.push({ key: 'repairRemark', label: 'Repair Remarks', sortable: true, collapsible: true });
      fields.push({ key: 'dateResolved', label: 'Date Resolved', sortable: true });
      return fields;
    }
  },
  watch: {
    filterBy(value) {
      let query = null;
      if (value != null) {
        query = { filterBy: value };
      }
      this.$router.push({ query }).catch(() => {});
    }
  },
  created() {
    this.fetchUnitDvirDefects();
  },
  methods: {
    ...mapActions([UnitActions.FETCH_UNIT_DVIR_DEFECTS]),
    async fetchUnitDvirDefects() {
      try {
        this.loading = true;
        await this[UnitActions.FETCH_UNIT_DVIR_DEFECTS](this.$route.params.unitId);
      } catch (error) {
        if (error.response && error.response.status == 404) {
          this.notFound = true;
        }
      } finally {
        this.loading = false;
      }
    },
    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },
    daysAgo(defect) {
      if (defect.dateResolved != null) return this.NA;
      else return moment().diff(defect.dateReported, 'days');
    },
    saftyBadgeVariant(isSafe) {
      if (isSafe === null) return 'secondary';
      return isSafe ? 'success' : 'danger';
    },
    safetyText(isSafe) {
      if (isSafe == null) return 'Unknown';
      return isSafe ? 'Safe' : 'Unsafe';
    }
  }
};
</script>
